<template>
  <div>
    <!-- ===================== LIST ===================== -->
    <div v-if="view === 'list'">
      <div class="card mar-to-15" style="width: calc(100% - 20px); margin-left: 10px; margin-right: 10px;">
        <div class="card-content">
          <div class="text-right">
            <p>
              <button class="btn add" @click.prevent="openForm(null)">
                <img src="/static/img/icons/add.png">
                {{ $t('deliveryPreferences.preferenceAdd') }}
              </button>
            </p>
          </div>

          <div v-loading="loading">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th style="width:70px">ID</th>
                  <th>{{ $t('deliveryPreferences.numeAdmin') }}</th>
                  <th>{{ $t('deliveryPreferences.name') }}</th>
                  <th>{{ $t('deliveryPreferences.type') }}</th>
                  <th>{{ $t('deliveryPreferences.enabled') }}</th>
                  <th style="width:130px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pref in preferences" :key="pref.id" style="cursor:pointer" @click="openForm(pref.id)">
                  <td>{{ pref.id }}</td>
                  <td>{{ pref.nume_admin }}</td>
                  <td>{{ pref.name }}</td>
                  <td>{{ pref.type === 2 ? $t('deliveryPreferences.typeSlot') : $t('deliveryPreferences.typeClassic') }}</td>
                  <td><i class="fa" :class="pref.enabled ? 'fa-check text-success' : 'fa-times text-danger'"></i></td>
                  <td><div class="edit" style="white-space:nowrap;"><i class="fa fa-pencil"></i> {{ $t('general.edit') }}</div></td>
                </tr>
                <tr v-if="!loading && !preferences.length">
                  <td colspan="6" class="text-center">{{ $t('deliveryPreferences.noData') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== FORM ===================== -->
    <div v-else>
      <div class="settings-top-bar">
        <div class="top-bar">
          <p>
            <a class="btn btn-orange btn-icon back" @click.prevent="backToList">
              <i class="ti-arrow-left"></i> {{ $t('deliveryPreferences.back') }}
            </a>
          </p>
          <p class="d-inline-flex">
            <a v-if="model.id" class="btn btn-danger btn-icon delete mar-rt-10" style="padding: 7px 10px; margin-right: 20px;" @click.prevent="remove">
              <img src="/static/img/icons/icon-close.png"> {{ $t('general.delete') }}
            </a>
            <button class="btn btn-icon btn-success save mar-rt-10" @click.prevent="save">
              <img src="/static/img/icons/save.png"> {{ $t('general.save') }}
            </button>
          </p>
        </div>
      </div>

      <div class="row mar-to-15" v-loading="loading">
        <!-- General -->
        <div class="col-lg-6 col-md-12">
          <div class="card" style="margin: 0 10px;">
            <div class="card-content">
              <p class="text-danger" v-if="formError">{{ formError }}</p>

              <div class="form-group">
                <label>{{ $t('deliveryPreferences.numeAdmin') }}</label>
                <input type="text" class="form-control" v-model="model.nume_admin" :placeholder="$t('deliveryPreferences.numeAdminPlaceholder')">
              </div>
              <div class="form-group">
                <label>{{ $t('deliveryPreferences.name') }} *</label>
                <input type="text" class="form-control" :class="{'is-invalid': attemptedSave && !model.name}" v-model="model.name">
              </div>
              <div class="form-group">
                <label style="display:block;" :class="{'text-danger': attemptedSave && model.type !== 1 && model.type !== 2}">{{ $t('deliveryPreferences.type') }}</label>
                <p-radio v-model="model.type" :label="1" :inline="true">{{ $t('deliveryPreferences.typeClassic') }}</p-radio>
                <p-radio v-model="model.type" :label="2" :inline="true">{{ $t('deliveryPreferences.typeSlot') }}</p-radio>
                <small class="text-muted" style="display:block; margin-top:6px;" v-if="model.type === 1">{{ $t('deliveryPreferences.typeClassicHelp') }}</small>
                <small class="text-muted" style="display:block; margin-top:6px;" v-if="model.type === 2">{{ $t('deliveryPreferences.typeSlotHelp') }}</small>
                <small class="text-danger" style="display:block; margin-top:6px;" v-if="attemptedSave && model.type !== 1 && model.type !== 2">{{ $t('general.required') }}</small>
              </div>
              <div class="form-group">
                <label style="margin-right: 10px;">{{ $t('deliveryPreferences.enabled') }}</label>
                <p-switch v-model="model.enabled" type="success"></p-switch>
              </div>
            </div>
          </div>
        </div>

        <!-- Zones (both types) -->
        <div class="col-lg-6 col-md-12" v-if="model.type === 1 || model.type === 2">
          <div class="card" style="margin: 0 10px;">
            <div class="card-content">
              <label>{{ $t('deliveryPreferences.zones') }}</label>
              <div v-for="(z, index) in model.zones" :key="index" class="d-flex align-items-center" style="display:flex; align-items:center; gap:8px; margin-bottom:8px;">
                <el-select
                  v-model="z.delivery_range_id"
                  filterable
                  :placeholder="$t('deliveryPreferences.selectZone')"
                  :class="{'is-invalid': isZoneDuplicate(index) || (attemptedSave && z.delivery_range_id === null)}"
                  style="flex:1; min-width:0;">
                  <el-option
                    v-for="zone in zones"
                    :key="zone.id"
                    :label="zone.name"
                    :value="zone.id"
                    :disabled="isZoneUsed(zone.id, index)">
                  </el-option>
                </el-select>
                <button class="btn btn-sm btn-default" style="width:35px; height:35px; padding:0; flex-shrink:0;" @click.prevent="moveZone(index, -1)" :disabled="index === 0"><i class="ti-arrow-up"></i></button>
                <button class="btn btn-sm btn-default" style="width:35px; height:35px; padding:0; flex-shrink:0;" @click.prevent="moveZone(index, 1)" :disabled="index === model.zones.length - 1"><i class="ti-arrow-down"></i></button>
                <button class="btn btn-sm btn-danger" style="width:35px; height:35px; padding:0; flex-shrink:0;" :disabled="model.zones.length <= 1" @click.prevent="removeZone(index)"><i class="ti-close"></i></button>
              </div>
              <button class="btn btn-sm btn-outline-primary" @click.prevent="addZone"><i class="ti-plus"></i> {{ $t('deliveryPreferences.selectZone') }}</button>
            </div>
          </div>
        </div>

        <!-- ============ CLASSIC (type 1) ============ -->
        <div class="col-lg-12 col-md-12" v-if="model.type === 1">
          <div class="card" style="margin: 15px 10px 0;">
            <div class="card-content">
              <h5>{{ $t('deliveryPreferences.typeClassic') }}</h5>

              <div class="form-group">
                <label style="margin-right: 10px;">{{ $t('deliveryPreferences.isAlwaysOpen') }}</label>
                <p-switch v-model="model.is_always_open" type="success"></p-switch>
              </div>

              <label>{{ $t('deliveryPreferences.availableDays') }}</label>
              <div class="changeDeliveryDisponibility">
                <div>
                  <p-radio v-model="model.hour_mode" :label="1" :inline="true">{{ $t('deliveryPreferences.hourModeHourDate') }}</p-radio>
                  <p-radio v-model="model.hour_mode" :label="2" :inline="true">{{ $t('deliveryPreferences.hourModeDateOnly') }}</p-radio>
                  <p-radio v-model="model.hour_mode" :label="3" :inline="true">{{ $t('deliveryPreferences.hourModeDisabled') }}</p-radio>
                </div>
                <small class="text-muted" style="display:block; margin-top:6px;" v-if="model.hour_mode === 1">{{ $t('deliveryPreferences.hourModeHourDateHelp') }}</small>
                <small class="text-muted" style="display:block; margin-top:6px;" v-if="model.hour_mode === 2">{{ $t('deliveryPreferences.hourModeDateOnlyHelp') }}</small>
                <small class="text-muted" style="display:block; margin-top:6px;" v-if="model.hour_mode === 3">{{ $t('deliveryPreferences.hourModeDisabledHelp') }}</small>
                <hr/>
              </div>

              <template v-if="model.hour_mode !== 3">
                <label for="" style="margin-bottom: 12px; margin-right: 10px;">
                  {{ $t('settingsOrdering.offset') }}
                  <input type="number" min="0" class="form-control" v-model.number="model.offset" :disabled="model.is_always_open">
                </label>
                <label for="" style="margin-bottom: 12px;">
                  {{ $t('settingsOrdering.period') }}
                  <input type="number" min="1" class="form-control" v-model.number="model.period" :disabled="model.is_always_open">
                </label>
                <small class="text-muted" style="display:block; margin-bottom:12px;">{{ $t('deliveryPreferences.offsetPeriodHelp') }}</small>
              </template>

              <template v-if="model.hour_mode !== 3">
              <div class="row" v-for="(day, dayIdx) in model.available_days" :key="day.day" style="display:flex; align-items:flex-start; margin-bottom:20px;">
                <div class="col-md-6">
                  <div class="row" style="margin-bottom:12px;">
                    <div class="col-md-3 padd in-inline-160">
                      <div class="checkbox">
                        <input type="checkbox" :id="'cd_' + day.day" v-model="day.open" :disabled="model.is_always_open">
                        <label :for="'cd_' + day.day">{{ $t('days.' + day.day) | capitalize }}</label>
                      </div>
                    </div>
                    <div class="col-md-9 padd">
                      from
                      <el-time-select :class="{'is-invalid': attemptedSave && day.open && !model.is_always_open && timePairInvalid(day.open_from, day.open_to)}" :disabled="!day.open || model.is_always_open" placeholder="Select time" v-model="day.open_from" :picker-options="pickerOptions"></el-time-select>
                      to
                      <el-time-select :class="{'is-invalid': attemptedSave && day.open && !model.is_always_open && timePairInvalid(day.open_from, day.open_to)}" :disabled="!day.open || model.is_always_open" placeholder="Select time" v-model="day.open_to" :picker-options="pickerOptions"></el-time-select>
                    </div>
                  </div>
                  <div class="row intervals" v-if="day.intervals">
                    <template v-for="(interval, intervalKey) in day.intervals">
                      <div class="col-md-3 padd in-inline-160"></div>
                      <div class="col-md-9 padd" style="margin-bottom:12px;">
                        from
                        <el-time-select :class="{'is-invalid': attemptedSave && day.open && !model.is_always_open && timePairInvalid(interval.open_from, interval.open_to)}" :disabled="!day.open || model.is_always_open" placeholder="Select time" v-model="interval.open_from" :picker-options="pickerOptions"></el-time-select>
                        to
                        <el-time-select :class="{'is-invalid': attemptedSave && day.open && !model.is_always_open && timePairInvalid(interval.open_from, interval.open_to)}" :disabled="!day.open || model.is_always_open" placeholder="Select time" v-model="interval.open_to" :picker-options="pickerOptions"></el-time-select>
                        <button class="btn btn-sm btn-danger" style="width:35px; height:35px; padding:0; margin-left:8px;" :disabled="model.is_always_open" @click.prevent="deleteClassicInterval(intervalKey, dayIdx)"><i class="ti-close"></i></button>
                      </div>
                    </template>
                  </div>
                </div>
                <div class="col-md-2">
                  <button class="btn add" :disabled="model.is_always_open" @click.prevent="addClassicInterval(dayIdx)" :title="$t('general.add')">
                    <img src="/static/img/icons/add.png"> {{ $t('general.add') }}
                  </button>
                </div>
              </div>
              </template>

              <hr/>
              <div class="row">
                <div class="col-md-4 form-group">
                  <label>{{ $t('deliveryPreferences.minimumOrderValue') }}</label>
                  <input type="number" step="0.01" min="0" class="form-control" v-model.number="model.minimum_order_value">
                </div>
                <div class="col-md-4 form-group">
                  <label>{{ $t('deliveryPreferences.defaultWaitingTime') }}</label>
                  <input type="number" step="1" min="0" class="form-control" v-model.number="model.default_waiting_time">
                </div>
              </div>
              <div class="form-group">
                <label style="margin-right: 10px;">{{ $t('deliveryPreferences.noPreorders') }}</label>
                <p-switch v-model="model.no_preorders" type="success"></p-switch>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ SLOT (type 2) ============ -->
        <div class="col-lg-12 col-md-12" v-if="model.type === 2">
          <div class="card" style="margin: 15px 10px 0;">
            <div class="card-content">
              <div class="form-group" style="max-width:320px;">
                <label>{{ $t('deliveryPreferences.delayHours') }}</label>
                <input type="number" step="1" min="0" class="form-control" v-model.number="model.delay_hours">
                <small class="text-muted">{{ $t('deliveryPreferences.delayHoursHelp') }}</small>
              </div>

              <div class="form-group">
                <label style="margin-right: 10px;">{{ $t('deliveryPreferences.autoAssignFirstSlot') }}</label>
                <p-switch v-model="model.auto_assign_first_slot" type="success"></p-switch>
              </div>

              <template v-if="!model.auto_assign_first_slot">
                <label for="" style="margin-bottom: 12px; margin-right: 10px;">
                  {{ $t('settingsOrdering.offset') }}
                  <input type="number" min="0" class="form-control" v-model.number="model.offset">
                </label>
                <label for="" style="margin-bottom: 12px;">
                  {{ $t('settingsOrdering.period') }}
                  <input type="number" min="1" class="form-control" v-model.number="model.period">
                </label>
                <small class="text-muted" style="display:block; margin-bottom:12px;">{{ $t('deliveryPreferences.offsetPeriodHelp') }}</small>
              </template>

              <label>{{ $t('deliveryPreferences.schedule') }}</label>
              <div class="row" v-for="d in days" :key="d.key" style="display:flex; align-items:flex-start; margin-bottom:20px;">
                <div class="col-md-2 padd in-inline-160">
                  <strong>{{ $t('deliveryPreferences.' + d.slug) }}</strong>
                </div>
                <div class="col-md-8 padd">
                  <div class="row intervals" v-for="row in rowsForDay(d.key)" :key="row._k" style="display:flex; align-items:center; margin-bottom:10px;">
                    <p-switch v-model="row.active" type="success" :title="$t('deliveryPreferences.active')" style="margin-right:10px;"></p-switch>
                    <select class="form-control" style="max-width:200px; margin-right:10px; padding-right:28px; text-overflow:ellipsis;" :class="{'is-invalid': attemptedSave && row.active && row.delivery_slot_id === null}" v-model.number="row.delivery_slot_id">
                      <option :value="null" disabled>{{ $t('deliveryPreferences.selectSlot') }}</option>
                      <option v-for="slot in slots" :key="slot.id" :value="slot.id">{{ slot.nume_admin || slot.name }}</option>
                    </select>
                    from
                    <el-time-select :class="{'is-invalid': attemptedSave && row.active && timePairInvalid(row.start_time, row.end_time)}" style="margin:0 8px;" placeholder="Select time" v-model="row.start_time" :picker-options="pickerOptions"></el-time-select>
                    to
                    <el-time-select :class="{'is-invalid': attemptedSave && row.active && timePairInvalid(row.start_time, row.end_time)}" style="margin:0 8px;" placeholder="Select time" v-model="row.end_time" :picker-options="pickerOptions"></el-time-select>
                    <button class="btn btn-sm btn-danger" style="width:35px; height:35px; padding:0; margin-left:8px;" @click.prevent="removeScheduleRow(row)"><i class="ti-close"></i></button>
                  </div>
                  <p v-if="!rowsForDay(d.key).length" class="text-muted" style="margin:0;">—</p>
                </div>
                <div class="col-md-2 padd">
                  <button class="btn add" @click.prevent="addScheduleRow(d.key)" :title="$t('general.add')">
                    <img src="/static/img/icons/add.png"> {{ $t('general.add') }}
                  </button>
                </div>
              </div>

              <div class="override-block">
                <h5 style="margin:0 0 4px;">{{ $t('deliveryPreferences.closedDates') }}</h5>
                <small class="text-muted" style="display:block; margin-bottom:14px;">{{ $t('deliveryPreferences.closedDatesHelp') }}</small>

                <div class="override-card" v-for="(cd, index) in model.closed_dates" :key="cd._k">
                  <div class="override-card-head">
                    <input type="date" class="form-control override-date" :class="{'is-invalid': attemptedSave && (!cd.date || closedDateDuplicate(index))}" v-model="cd.date">
                    <span class="override-weekday">{{ weekdayLabel(cd.date) }}</span>
                    <div class="override-toggle">
                      <button type="button" class="closed" :class="{active: cd.all_day}" @click.prevent="cd.all_day = 1">
                        <i class="ti-na"></i> {{ $t('deliveryPreferences.closedAllDay') }}
                      </button>
                      <button type="button" class="partial" :class="{active: !cd.all_day}" @click.prevent="setOverrideSchedule(cd)">
                        <i class="ti-calendar"></i> {{ $t('deliveryPreferences.closedOverrideSchedule') }}
                      </button>
                    </div>
                    <button class="btn btn-sm btn-danger override-remove" @click.prevent="removeClosedDate(index)"><i class="ti-close"></i></button>
                  </div>

                  <div class="override-card-body">
                    <span v-if="cd.all_day" class="text-muted override-none">{{ $t('deliveryPreferences.closedNoDelivery') }}</span>
                    <template v-else>
                      <div class="row intervals" v-for="row in cd.schedules" :key="row._k" style="display:flex; align-items:center; margin:0 0 10px;">
                        <p-switch v-model="row.active" type="success" :title="$t('deliveryPreferences.active')" style="margin-right:10px;"></p-switch>
                        <select class="form-control" style="max-width:200px; margin-right:10px; padding-right:28px; text-overflow:ellipsis;" :class="{'is-invalid': attemptedSave && row.active && row.delivery_slot_id === null}" v-model.number="row.delivery_slot_id">
                          <option :value="null" disabled>{{ $t('deliveryPreferences.selectSlot') }}</option>
                          <option v-for="slot in slots" :key="slot.id" :value="slot.id">{{ slot.nume_admin || slot.name }}</option>
                        </select>
                        from
                        <el-time-select :class="{'is-invalid': attemptedSave && row.active && timePairInvalid(row.start_time, row.end_time)}" style="margin:0 8px;" placeholder="Select time" v-model="row.start_time" :picker-options="pickerOptions"></el-time-select>
                        to
                        <el-time-select :class="{'is-invalid': attemptedSave && row.active && timePairInvalid(row.start_time, row.end_time)}" style="margin:0 8px;" placeholder="Select time" v-model="row.end_time" :picker-options="pickerOptions"></el-time-select>
                        <button class="btn btn-sm btn-danger" style="width:35px; height:35px; padding:0; margin-left:8px;" @click.prevent="removeOverrideRow(cd, row)"><i class="ti-close"></i></button>
                      </div>
                      <p v-if="!cd.schedules.length" class="text-muted" :class="{'text-danger': attemptedSave}" style="margin:0 0 10px;">{{ $t('deliveryPreferences.closedScheduleEmpty') }}</p>
                      <div class="override-actions">
                        <button class="btn add" @click.prevent="addOverrideRow(cd)">
                          <img src="/static/img/icons/add.png"> {{ $t('general.add') }}
                        </button>
                        <button v-if="cd.date" class="btn btn-sm btn-default" @click.prevent="copyWeeklyInto(cd, true)">
                          <i class="ti-files"></i> {{ $t('deliveryPreferences.closedCopyWeekly', { day: weekdayLabel(cd.date) }) }}
                        </button>
                      </div>
                    </template>
                  </div>
                </div>
                <p v-if="!model.closed_dates.length" class="text-muted" style="margin:0 0 12px;">{{ $t('deliveryPreferences.closedEmpty') }}</p>

                <button class="btn add" style="margin-top:12px;" @click.prevent="addClosedDate">
                  <img src="/static/img/icons/add.png"> {{ $t('deliveryPreferences.addClosedDate') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Vue from 'vue'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'
import { TimeSelect, Select, Option } from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'

Vue.use(TimeSelect)
Vue.use(Select)
Vue.use(Option)

const DAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

// Build the classic "available days" structure (one object per weekday) exactly like
// the delivery method editor, keeping any already-saved open/hours/intervals values.
function buildAvailableDays (existing) {
  const source = Array.isArray(existing) ? existing : []
  return DAY_NAMES.map(function (name) {
    const found = source.find(function (d) { return d && d.day === name })
    if (found) {
      return {
        day: name,
        open: !!found.open,
        open_from: found.open_from || '',
        open_to: found.open_to || '',
        intervals: Array.isArray(found.intervals) ? found.intervals.map(function (iv) { return { open_from: iv.open_from || '', open_to: iv.open_to || '' } }) : []
      }
    }
    return { day: name, open: false, open_from: '', open_to: '', intervals: [] }
  })
}

export default {
  components: {
    PSwitch
  },
  data () {
    return {
      view: 'list',
      loading: true,
      attemptedSave: false,
      formError: '',
      preferences: [],
      slots: [],
      zones: [],
      rowKey: 0,
      pickerOptions: { start: '00:00', step: '00:15', end: '24:00' },
      days: [
        { key: 1, slug: 'mon' },
        { key: 2, slug: 'tue' },
        { key: 3, slug: 'wed' },
        { key: 4, slug: 'thu' },
        { key: 5, slug: 'fri' },
        { key: 6, slug: 'sat' },
        { key: 0, slug: 'sun' }
      ],
      model: this.emptyModel()
    }
  },
  created () {
    this.getSlots()
    this.getZones()
    this.getData()
  },
  methods: {
    sessionData (extra = {}) {
      return Object.assign({
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }, extra)
    },
    emptyModel () {
      return {
        id: null,
        nume_admin: '',
        name: '',
        type: null,
        enabled: true,
        sort_order: 0,
        hour_mode: 1,
        available_days: buildAvailableDays([]),
        offset: 0,
        period: 1,
        no_preorders: false,
        is_always_open: false,
        is_always_open_preorder_hours: 23, // hardcoded, no longer editable in the UI
        minimum_order_value: 0,
        default_waiting_time: 0,
        delay_hours: 0,
        auto_assign_first_slot: false,
        schedules: [],
        closed_dates: [],
        zones: [{ delivery_range_id: null }]
      }
    },
    getData () {
      this.loading = true
      this.apiRequest('/delivery-preference', 'GET', this.sessionData(), (response) => {
        if (response.status === true) {
          this.preferences = response.data
        }
        this.loading = false
      })
    },
    getSlots () {
      this.apiRequest('/delivery-slot', 'GET', this.sessionData(), (response) => {
        if (response.status === true) this.slots = response.data
      })
    },
    getZones () {
      this.apiRequest('/delivery-zone', 'GET', this.sessionData(), (response) => {
        if (response.status === true) {
          // Only company-level zones (location_id null) can be attached to a preference.
          this.zones = (response.data || []).filter(z => z.editable)
        }
      })
    },
    openForm (id) {
      this.formError = ''
      this.attemptedSave = false
      if (id) {
        const pref = this.preferences.find(p => p.id === id)
        const m = this.emptyModel()
        m.id = pref.id
        m.nume_admin = pref.nume_admin
        m.name = pref.name
        m.type = Number(pref.type)
        m.enabled = !!pref.enabled
        m.sort_order = Number(pref.sort_order || 0)
        m.hour_mode = pref.hour_mode !== null ? Number(pref.hour_mode) : 1
        m.available_days = buildAvailableDays(pref.available_days)
        m.offset = Number(pref.offset || 0)
        m.period = Number(pref.period || 1)
        m.no_preorders = !!pref.no_preorders
        m.is_always_open = !!pref.is_always_open
        m.is_always_open_preorder_hours = 23 // hardcoded, no longer editable in the UI
        m.minimum_order_value = Number(pref.minimum_order_value || 0)
        m.default_waiting_time = Number(pref.default_waiting_time || 0)
        m.delay_hours = Number(pref.delay_hours || 0)
        m.auto_assign_first_slot = !!pref.auto_assign_first_slot
        m.schedules = (pref.schedules || []).map(s => ({
          _k: this.rowKey++,
          id: s.id,
          day_of_week: Number(s.day_of_week),
          delivery_slot_id: s.delivery_slot_id ? Number(s.delivery_slot_id) : null,
          start_time: this.trimTime(s.start_time),
          end_time: this.trimTime(s.end_time),
          active: !!s.active
        }))
        m.closed_dates = (pref.closed_dates || []).map(c => ({
          _k: this.rowKey++,
          date: (c.date || '').substr(0, 10),
          // legacy rows (no all_day column yet) are whole-day closures
          all_day: c.all_day === undefined || c.all_day === null || !!c.all_day ? 1 : 0,
          // "Suprascrie program": the date's own slot rows replacing the weekly schedule
          schedules: (c.schedules || []).map(s => ({
            _k: this.rowKey++,
            id: s.id,
            delivery_slot_id: s.delivery_slot_id ? Number(s.delivery_slot_id) : null,
            start_time: this.trimTime(s.start_time),
            end_time: this.trimTime(s.end_time),
            active: !!s.active
          }))
        }))
        m.zones = (pref.preference_zones || pref.preferenceZones || []).map(z => ({
          delivery_range_id: z.delivery_range_id !== null ? Number(z.delivery_range_id) : null
        }))
        if (!m.zones.length) m.zones.push({ delivery_range_id: null })
        this.model = m
      } else {
        this.model = this.emptyModel()
      }
      this.view = 'form'
    },
    trimTime (t) {
      if (!t) return null
      return String(t).substr(0, 5) // HH:MM
    },
    backToList () {
      this.view = 'list'
      this.getData()
    },
    // zones
    addZone () { this.model.zones.push({ delivery_range_id: null }) },
    removeZone (index) { if (this.model.zones.length > 1) this.model.zones.splice(index, 1) },
    // true if this zone value is already picked in another row (used to disable options)
    isZoneUsed (rangeId, currentIndex) {
      return this.model.zones.some((z, i) => i !== currentIndex && z.delivery_range_id === rangeId)
    },
    // true for the 2nd+ occurrence of a real zone (marks the duplicate row red; null = unselected, ignored)
    isZoneDuplicate (index) {
      const id = this.model.zones[index].delivery_range_id
      if (id === null || typeof id === 'undefined') return false
      return this.model.zones.findIndex(z => z.delivery_range_id === id) !== index
    },
    hasDuplicateZones () {
      const ids = this.model.zones.map(z => z.delivery_range_id).filter(v => v !== null && typeof v !== 'undefined')
      return ids.some((v, i) => ids.indexOf(v) !== i)
    },
    // A "HH:MM" pair is invalid if either side is empty or start is not before end.
    timePairInvalid (from, to) {
      if (!from || !to) return true
      return from >= to
    },
    // Returns the first schedule/hours error message, or '' if the schedule is valid.
    scheduleError () {
      if (this.model.type === 1) {
        // Classic: only open days, and only when hours actually apply (not "always open").
        if (this.model.is_always_open) return ''
        for (let i = 0; i < this.model.available_days.length; i++) {
          const day = this.model.available_days[i]
          if (!day.open) continue
          if (!day.open_from || !day.open_to) return this.$t('deliveryPreferences.hoursRequired')
          if (day.open_from >= day.open_to) return this.$t('deliveryPreferences.hoursOrder')
          const intervals = day.intervals || []
          for (let j = 0; j < intervals.length; j++) {
            const iv = intervals[j]
            if (!iv.open_from || !iv.open_to) return this.$t('deliveryPreferences.hoursRequired')
            if (iv.open_from >= iv.open_to) return this.$t('deliveryPreferences.hoursOrder')
          }
        }
      } else if (this.model.type === 2) {
        // Slot: only active rows.
        for (let i = 0; i < this.model.schedules.length; i++) {
          const row = this.model.schedules[i]
          if (!row.active) continue
          if (row.delivery_slot_id === null || typeof row.delivery_slot_id === 'undefined') return this.$t('deliveryPreferences.slotRequired')
          if (!row.start_time || !row.end_time) return this.$t('deliveryPreferences.hoursRequired')
          if (row.start_time >= row.end_time) return this.$t('deliveryPreferences.hoursOrder')
        }
        // Calendar overrides: unique dates; an overridden schedule follows the weekly rules.
        for (let i = 0; i < this.model.closed_dates.length; i++) {
          const cd = this.model.closed_dates[i]
          if (!cd.date) continue
          if (this.closedDateDuplicate(i)) return this.$t('deliveryPreferences.closedDateDuplicate')
          if (cd.all_day) continue
          if (!cd.schedules.length) return this.$t('deliveryPreferences.closedScheduleRequired')
          for (let j = 0; j < cd.schedules.length; j++) {
            const row = cd.schedules[j]
            if (!row.active) continue
            if (row.delivery_slot_id === null || typeof row.delivery_slot_id === 'undefined') return this.$t('deliveryPreferences.slotRequired')
            if (!row.start_time || !row.end_time) return this.$t('deliveryPreferences.hoursRequired')
            if (row.start_time >= row.end_time) return this.$t('deliveryPreferences.hoursOrder')
          }
        }
      }
      return ''
    },
    moveZone (index, dir) {
      const to = index + dir
      if (to < 0 || to >= this.model.zones.length) return
      const arr = this.model.zones
      const tmp = arr[index]
      this.$set(arr, index, arr[to])
      this.$set(arr, to, tmp)
    },
    // schedule
    rowsForDay (dayKey) {
      return this.model.schedules.filter(s => s.day_of_week === dayKey)
    },
    addScheduleRow (dayKey) {
      this.model.schedules.push({
        _k: this.rowKey++,
        day_of_week: dayKey,
        delivery_slot_id: null,
        start_time: '08:00',
        end_time: '10:00',
        active: true
      })
    },
    removeScheduleRow (row) {
      const i = this.model.schedules.indexOf(row)
      if (i > -1) this.model.schedules.splice(i, 1)
    },
    // closed dates
    addClosedDate () { this.model.closed_dates.push({ _k: this.rowKey++, date: '', all_day: 1, schedules: [] }) },
    closedDateDuplicate (index) {
      const date = this.model.closed_dates[index].date
      return !!date && this.model.closed_dates.some((c, i) => i !== index && c.date === date)
    },
    removeClosedDate (index) { this.model.closed_dates.splice(index, 1) },
    // 0 = Sunday .. 6 = Saturday for a 'YYYY-MM-DD' date (local, no timezone shift)
    dayOfWeek (date) {
      const p = String(date || '').split('-').map(Number)
      if (p.length !== 3 || !p[0] || !p[1] || !p[2]) return null
      return new Date(p[0], p[1] - 1, p[2]).getDay()
    },
    weekdayLabel (date) {
      const dow = this.dayOfWeek(date)
      const d = this.days.find(x => x.key === dow)
      return d ? this.$t('deliveryPreferences.' + d.slug) : ''
    },
    // Switching to "Suprascrie program" starts from that weekday's weekly schedule.
    setOverrideSchedule (cd) {
      cd.all_day = 0
      if (!cd.schedules.length) this.copyWeeklyInto(cd, false)
    },
    copyWeeklyInto (cd, confirmReplace) {
      const dow = this.dayOfWeek(cd.date)
      if (dow === null) return
      const apply = () => {
        cd.schedules = this.rowsForDay(dow).map(s => ({
          _k: this.rowKey++,
          delivery_slot_id: s.delivery_slot_id,
          start_time: s.start_time,
          end_time: s.end_time,
          active: s.active
        }))
      }
      if (!confirmReplace || !cd.schedules.length) return apply()
      swal({
        title: this.$t('deliveryPreferences.closedCopyConfirm', { day: this.weekdayLabel(cd.date) }),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes')
      }).then((result) => {
        if (result && (result.value || result === true)) apply()
      }).catch(() => {})
    },
    addOverrideRow (cd) {
      cd.schedules.push({ _k: this.rowKey++, delivery_slot_id: null, start_time: '08:00', end_time: '10:00', active: true })
    },
    removeOverrideRow (cd, row) {
      const i = cd.schedules.indexOf(row)
      if (i > -1) cd.schedules.splice(i, 1)
    },
    // classic available-days extra time intervals (same behaviour as the method editor)
    addClassicInterval (dayIdx) {
      const day = this.model.available_days[dayIdx]
      if (!day.intervals) this.$set(day, 'intervals', [])
      day.intervals.push({ open_from: '', open_to: '' })
    },
    deleteClassicInterval (intervalKey, dayIdx) {
      this.model.available_days[dayIdx].intervals.splice(intervalKey, 1)
    },
    save () {
      this.attemptedSave = true
      if (this.model.type !== 1 && this.model.type !== 2) {
        this.formError = this.$t('general.fillRequiredFields')
        return
      }
      if (!this.model.name) {
        this.formError = this.$t('general.fillRequiredFields')
        return
      }
      if (!this.model.zones.length || this.model.zones.some(z => z.delivery_range_id === null || typeof z.delivery_range_id === 'undefined')) {
        this.formError = this.$t('deliveryPreferences.zoneRequired')
        return
      }
      if (this.hasDuplicateZones()) {
        this.formError = this.$t('deliveryPreferences.duplicateZone')
        return
      }
      const schedErr = this.scheduleError()
      if (schedErr) {
        this.formError = schedErr
        return
      }
      this.formError = ''
      this.loading = true

      const payload = this.sessionData({
        name: this.model.name,
        nume_admin: this.model.nume_admin,
        type: this.model.type,
        enabled: this.model.enabled,
        sort_order: this.model.sort_order,
        hour_mode: this.model.hour_mode,
        available_days: this.model.available_days,
        offset: this.model.offset,
        period: this.model.period,
        no_preorders: this.model.no_preorders,
        is_always_open: this.model.is_always_open,
        is_always_open_preorder_hours: 23,
        minimum_order_value: this.model.minimum_order_value,
        default_waiting_time: this.model.default_waiting_time,
        delay_hours: this.model.delay_hours,
        auto_assign_first_slot: this.model.auto_assign_first_slot,
        schedules: this.model.schedules.map(s => ({
          id: s.id,
          day_of_week: s.day_of_week,
          delivery_slot_id: s.delivery_slot_id,
          start_time: s.start_time,
          end_time: s.end_time,
          active: s.active
        })),
        closed_dates: this.model.closed_dates.filter(c => !!c.date).map(c => ({
          date: c.date,
          all_day: !!c.all_day,
          schedules: c.all_day ? [] : c.schedules.map(s => ({
            id: s.id,
            delivery_slot_id: s.delivery_slot_id,
            start_time: s.start_time,
            end_time: s.end_time,
            active: s.active
          }))
        })),
        zones: this.model.zones.map((z, i) => ({ delivery_range_id: z.delivery_range_id, sort_order: i + 1 }))
      })

      const url = this.model.id ? '/delivery-preference/' + this.model.id : '/delivery-preference'
      const method = this.model.id ? 'PUT' : 'POST'

      this.apiRequest(url, method, payload, (response) => {
        this.loading = false
        if (response.success === true) {
          swal({ text: this.$t('deliveryPreferences.saved'), type: 'success', showConfirmButton: false, timer: 1500 }).then(() => {}).catch(() => {})
          this.backToList()
        } else {
          this.formError = this.firstError(response.errors)
        }
      })
    },
    remove () {
      const self = this
      swal({
        title: self.$t('deliveryPreferences.deleteConfirm'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: self.$t('general.yes')
      }).then((result) => {
        if (result && (result.value || result === true)) {
          self.loading = true
          self.apiRequest('/delivery-preference/' + self.model.id, 'PUT', self.sessionData({ delete: true }), (response) => {
            self.loading = false
            if (response.success === true) {
              swal({ text: self.$t('deliveryPreferences.saved'), type: 'success', showConfirmButton: false, timer: 1500 }).then(() => {}).catch(() => {})
              self.backToList()
            } else {
              swal({ text: self.firstError(response.errors) || self.$t('validations.wentWrong'), type: 'error', showConfirmButton: false, timer: 2500 }).then(() => {}).catch(() => {})
            }
          })
        }
      }).catch(() => {})
    },
    firstError (errors) {
      if (!errors) return ''
      const keys = Object.keys(errors)
      if (!keys.length) return ''
      const v = errors[keys[0]]
      return Array.isArray(v) ? v[0] : v
    }
  }
}
</script>

<style scoped>
.form-control.is-invalid,
select.is-invalid {
  border: 1px solid #dc3545 !important;
  background-color: #fff5f5;
}
/* calendar override ("Suprascriere calendaristică") table */
.override-block {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #ececf3;
}
.override-card {
  border: 1px solid #ececf3;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: hidden;
}
.override-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #f6f6fb;
}
.override-date { width: 170px; }
.override-weekday {
  min-width: 80px;
  font-weight: 600;
  color: #6b6b80;
}
.override-remove {
  width: 35px;
  height: 35px;
  padding: 0;
  margin-left: auto;
}
.override-card-body { padding: 12px 14px; }
.override-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.override-toggle {
  display: inline-flex;
  border: 1px solid #e0e0ea;
  border-radius: 8px;
  overflow: hidden;
}
.override-toggle button {
  border: 0;
  background: #fff;
  color: #6b6b80;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: background .15s, color .15s;
}
.override-toggle button + button { border-left: 1px solid #e0e0ea; }
.override-toggle button.closed.active { background: #fdecec; color: #d9534f; font-weight: 600; }
.override-toggle button.partial.active { background: #e3f6ef; color: #17a674; font-weight: 600; }
.override-none { font-style: italic; }
/* el-time-select is a wrapper component; reach its inner input */
.is-invalid >>> input,
.is-invalid >>> .el-input__inner {
  border-color: #dc3545 !important;
  background-color: #fff5f5;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Settings/DeliveryPreferences/SettingsDeliveryPreferences.vue